import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  MapPin, Clock, Users, IndianRupee, Wifi, Check, X, ChevronLeft, ChevronRight,
  Calendar, ArrowRight, Phone, Mail, Star, Shield, Loader2
} from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;
const LOGO_URL = '/logo.webp';

const SpacePreviewPage = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const ref = searchParams.get('ref') || '';

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedDate, setSelectedDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    return d.toISOString().split('T')[0];
  });
  const [availability, setAvailability] = useState(null);
  const [availLoading, setAvailLoading] = useState(false);
  const [currentImageIdx, setCurrentImageIdx] = useState(0);

  useEffect(() => {
    const fetchSpace = async () => {
      try {
        const res = await axios.get(`${API}/api/public/spaces/${id}${ref ? `?ref=${ref}` : ''}`);
        setData(res.data);
      } catch (err) {
        setError(err.response?.data?.detail || 'Space not found');
      } finally {
        setLoading(false);
      }
    };
    fetchSpace();
  }, [id, ref]);

  const fetchAvailability = useCallback(async () => {
    if (!selectedDate) return;
    setAvailLoading(true);
    try {
      const res = await axios.get(`${API}/api/public/spaces/${id}/availability?date=${selectedDate}`);
      setAvailability(res.data);
    } catch {
      setAvailability(null);
    } finally {
      setAvailLoading(false);
    }
  }, [id, selectedDate]);

  useEffect(() => {
    if (data) fetchAvailability();
  }, [data, fetchAvailability]);

  const handleBookNow = () => {
    const venue = data?.venue?.id;
    const params = new URLSearchParams({ venue, space: id });
    if (ref) params.set('ref', ref);
    navigate(`/login?redirect=${encodeURIComponent(`/booking?${params.toString()}`)}`);
  };

  const prevDate = () => {
    const d = new Date(selectedDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    d.setDate(d.getDate() - 1);
    if (d >= today) setSelectedDate(d.toISOString().split('T')[0]);
  };

  const nextDate = () => {
    const d = new Date(selectedDate);
    d.setDate(d.getDate() + 1);
    const max = new Date();
    max.setDate(max.getDate() + 30);
    if (d <= max) setSelectedDate(d.toISOString().split('T')[0]);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f0ebe0] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-primary animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#f0ebe0] flex items-center justify-center">
        <Card className="max-w-md w-full mx-4">
          <CardContent className="p-8 text-center">
            <X className="w-12 h-12 text-destructive mx-auto mb-4" />
            <h2 className="text-xl font-bold mb-2">Space Not Found</h2>
            <p className="text-muted-foreground">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const { space, venue } = data;
  const mode = space.booking_mode;
  const images = space.images?.length ? space.images : venue.images?.length ? venue.images : [];
  const rateLabel = mode === 'daily' ? '/day' : mode === 'monthly' ? '/month' : '/hr';
  const typeLabel = {
    meeting_room: 'Meeting Room',
    conference_room: 'Conference Room',
    day_pass: 'Day Pass',
    dedicated_desk: 'Dedicated Desk',
    event_space: 'Event Space',
    virtual_office: 'Virtual Office',
  }[space.bookable_type] || space.bookable_type;

  const formatDate = (dateStr) => {
    return new Date(dateStr + 'T00:00:00').toLocaleDateString('en-IN', {
      weekday: 'long', day: 'numeric', month: 'long', year: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-[#f0ebe0]">
      {/* Sticky Header */}
      <header className="sticky top-0 z-50 bg-white/90 backdrop-blur-md border-b border-[#e5dfd0] shadow-sm">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="Clayworks" className="h-8 w-auto" />
          </div>
          <Button onClick={handleBookNow} className="bg-primary hover:bg-primary/90 text-white" data-testid="header-book-now-btn">
            <Calendar className="w-4 h-4 mr-2" />
            Book Now
          </Button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8 space-y-8">
        {/* Image Gallery */}
        <div className="relative rounded-xl overflow-hidden bg-black/5 aspect-[16/7]" data-testid="space-image-gallery">
          {images.length > 0 ? (
            <>
              <img
                src={images[currentImageIdx]}
                alt={space.name}
                className="w-full h-full object-cover"
              />
              {images.length > 1 && (
                <>
                  <button
                    onClick={() => setCurrentImageIdx(i => (i - 1 + images.length) % images.length)}
                    className="absolute left-3 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full p-2 transition"
                    aria-label="Previous image"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => setCurrentImageIdx(i => (i + 1) % images.length)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full p-2 transition"
                    aria-label="Next image"
                  >
                    <ChevronRight className="w-5 h-5" />
                  </button>
                  <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
                    {images.map((_, i) => (
                      <button
                        key={i}
                        onClick={() => setCurrentImageIdx(i)}
                        className={`w-2 h-2 rounded-full transition ${i === currentImageIdx ? 'bg-white scale-125' : 'bg-white/50'}`}
                      />
                    ))}
                  </div>
                </>
              )}
            </>
          ) : (
            <div className="w-full h-full flex items-center justify-center text-muted-foreground">
              <Star className="w-12 h-12 opacity-20" />
            </div>
          )}
          {/* Type badge overlay */}
          <div className="absolute top-4 left-4">
            <Badge className="bg-primary/90 text-white text-sm px-3 py-1">{typeLabel}</Badge>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Left Column: Details */}
          <div className="lg:col-span-2 space-y-6">
            {/* Title & Price */}
            <div>
              <h1 className="text-3xl sm:text-4xl font-bold text-foreground tracking-tight" data-testid="space-name">
                {space.name}
              </h1>
              <div className="flex items-center gap-2 mt-2 text-muted-foreground">
                <MapPin className="w-4 h-4" />
                <span className="text-sm">{venue.name} — {venue.address}, {venue.city}</span>
              </div>
              <div className="flex items-center gap-4 mt-4">
                <div className="flex items-center gap-1 text-2xl font-bold text-primary" data-testid="space-price">
                  <IndianRupee className="w-5 h-5" />
                  {space.hourly_rate.toLocaleString('en-IN')}
                  <span className="text-sm font-normal text-muted-foreground">{rateLabel}</span>
                </div>
                {space.gst_rate > 0 && (
                  <span className="text-xs text-muted-foreground">+ {space.gst_rate}% GST</span>
                )}
              </div>
            </div>

            {/* Quick Info */}
            <div className="flex flex-wrap gap-3">
              <div className="flex items-center gap-1.5 text-sm bg-card rounded-lg px-3 py-2 border border-border">
                <Users className="w-4 h-4 text-primary" />
                <span>{space.capacity} {space.capacity === 1 ? 'seat' : 'seats'}</span>
              </div>
              <div className="flex items-center gap-1.5 text-sm bg-card rounded-lg px-3 py-2 border border-border">
                <Clock className="w-4 h-4 text-primary" />
                <span>{venue.opening_time} - {venue.closing_time}</span>
              </div>
              {mode === 'daily' && (
                <div className="flex items-center gap-1.5 text-sm bg-card rounded-lg px-3 py-2 border border-border">
                  <Calendar className="w-4 h-4 text-primary" />
                  <span>9:00 AM - 8:00 PM</span>
                </div>
              )}
            </div>

            {/* Description */}
            {space.description && (
              <div>
                <h3 className="text-lg font-semibold mb-2">About this space</h3>
                <p className="text-muted-foreground leading-relaxed">{space.description}</p>
              </div>
            )}

            {/* Amenities */}
            {(space.amenities?.length > 0 || venue.amenities?.length > 0) && (
              <div>
                <h3 className="text-lg font-semibold mb-3">Amenities</h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {[...new Set([...(space.amenities || []), ...(venue.amenities || [])])].map((a, i) => (
                    <div key={i} className="flex items-center gap-2 text-sm py-1.5">
                      <Wifi className="w-4 h-4 text-primary flex-shrink-0" />
                      <span>{a}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Inclusions / Exclusions */}
            {(space.inclusions?.length > 0 || space.exclusions?.length > 0) && (
              <div className="grid sm:grid-cols-2 gap-6">
                {space.inclusions?.length > 0 && (
                  <div>
                    <h3 className="text-lg font-semibold mb-3 text-green-700">Included</h3>
                    <ul className="space-y-2">
                      {space.inclusions.map((item, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm">
                          <Check className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {space.exclusions?.length > 0 && (
                  <div>
                    <h3 className="text-lg font-semibold mb-3 text-red-700">Not Included</h3>
                    <ul className="space-y-2">
                      {space.exclusions.map((item, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm">
                          <X className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            {/* Venue Info */}
            <Card className="border-border">
              <CardContent className="p-5">
                <h3 className="text-lg font-semibold mb-3">Venue Details</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-muted-foreground" />
                    <span>{venue.address}, {venue.city} {venue.pincode && `- ${venue.pincode}`}</span>
                  </div>
                  {venue.phone && (
                    <div className="flex items-center gap-2">
                      <Phone className="w-4 h-4 text-muted-foreground" />
                      <a href={`tel:${venue.phone}`} className="text-primary hover:underline">{venue.phone}</a>
                    </div>
                  )}
                  {venue.email && (
                    <div className="flex items-center gap-2">
                      <Mail className="w-4 h-4 text-muted-foreground" />
                      <a href={`mailto:${venue.email}`} className="text-primary hover:underline">{venue.email}</a>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right Column: Availability + Book */}
          <div className="space-y-4">
            {/* Availability Card */}
            <Card className="border-border sticky top-20" data-testid="availability-card">
              <CardContent className="p-5 space-y-4">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-primary" />
                  Availability
                </h3>

                {/* Date Navigator */}
                <div className="flex items-center justify-between bg-muted/50 rounded-lg p-2">
                  <button onClick={prevDate} className="p-1.5 rounded hover:bg-muted" data-testid="prev-date-btn">
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <div className="text-center">
                    <input
                      type="date"
                      value={selectedDate}
                      onChange={(e) => setSelectedDate(e.target.value)}
                      min={new Date().toISOString().split('T')[0]}
                      className="bg-transparent text-center text-sm font-medium cursor-pointer border-none outline-none"
                      data-testid="availability-date-input"
                    />
                    <p className="text-xs text-muted-foreground">{formatDate(selectedDate)}</p>
                  </div>
                  <button onClick={nextDate} className="p-1.5 rounded hover:bg-muted" data-testid="next-date-btn">
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>

                {/* Availability Status */}
                {availLoading ? (
                  <div className="flex justify-center py-4">
                    <Loader2 className="w-5 h-5 text-primary animate-spin" />
                  </div>
                ) : availability ? (
                  <div className="space-y-3">
                    {/* Summary */}
                    <div className={`text-center py-3 rounded-lg text-sm font-medium ${
                      availability.closed
                        ? 'bg-gray-100 text-gray-500 border border-gray-200'
                        : availability.is_available
                        ? 'bg-green-50 text-green-700 border border-green-200'
                        : 'bg-red-50 text-red-700 border border-red-200'
                    }`} data-testid="availability-status">
                      {availability.closed
                        ? (availability.message || 'Closed')
                        : availability.is_available ? (
                        mode === 'daily' || mode === 'monthly'
                          ? `${availability.available} of ${availability.capacity} spots available`
                          : `${availability.available} time slots available`
                      ) : (
                        'Fully booked for this date'
                      )}
                    </div>

                    {/* Hourly Slot Grid */}
                    {availability.slots?.length > 0 && (
                      <div>
                        <p className="text-xs text-muted-foreground mb-2">Time slots</p>
                        <div className="grid grid-cols-3 gap-1.5">
                          {availability.slots.map((slot, i) => (
                            <div
                              key={i}
                              className={`text-xs text-center py-1.5 px-1 rounded ${
                                slot.available
                                  ? 'bg-green-50 text-green-700 border border-green-200'
                                  : 'bg-red-50 text-red-400 border border-red-100 line-through'
                              }`}
                            >
                              {slot.start}
                            </div>
                          ))}
                        </div>
                        <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                          <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-green-100 border border-green-300" /> Available</span>
                          <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-red-100 border border-red-200" /> Booked</span>
                        </div>
                      </div>
                    )}
                  </div>
                ) : null}

                {/* CTA */}
                <Button
                  onClick={handleBookNow}
                  className="w-full bg-primary hover:bg-primary/90 text-white h-12 text-base"
                  data-testid="book-now-btn"
                >
                  Book Now
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
                <p className="text-xs text-center text-muted-foreground">
                  Log in or create an account to complete your booking
                </p>

                {/* Trust Signals */}
                <div className="border-t border-border pt-3 space-y-2">
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <Shield className="w-3.5 h-3.5" />
                    <span>Free cancellation up to 48 hours before</span>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <Check className="w-3.5 h-3.5" />
                    <span>Instant confirmation after payment</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-card border-t border-border mt-16 py-8">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 text-center text-sm text-muted-foreground">
          <p>&copy; {new Date().getFullYear()} Clayworks. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default SpacePreviewPage;
